import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import papi from "../lib/parentApi";
import { formatApiErrorDetail } from "../lib/api";
import { applyBrand, clearBrand } from "../design/brand";

const ParentAuthContext = createContext(null);
export const useParentAuth = () => useContext(ParentAuthContext);

export function ParentAuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authed
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Session lives in the httpOnly `parent_access_token` cookie.
      const { data } = await papi.get("/parent/auth/me");
      setParent(data);
    } catch {
      setParent(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // WL: theme the parent portal from the school's branding; revert on logout.
  useEffect(() => {
    if (parent && typeof parent === "object") applyBrand(parent.branding);
    else if (parent === false) clearBrand();
  }, [parent]);

  // Step 1: password validation -> server sends a code to email + mobile (mandatory 2FA). No session.
  const login = async (tenant_slug, identifier, password) => {
    const { data } = await papi.post("/parent/auth/login", { tenant_slug, identifier, password });
    return data;  // { otp_required, masked_email, masked_mobile, channels }
  };

  // Step 2: verify the code and complete login.
  const loginVerify = async (tenant_slug, identifier, otp) => {
    const { data } = await papi.post("/parent/auth/login/verify", { tenant_slug, identifier, otp });
    setParent(data.user);
    return data.user;
  };


  // Step 1: request a one-time code to the registered guardian phone.
  const claimStart = async (tenant_slug, phone) => {
    const { data } = await papi.post("/parent/auth/claim/start", { tenant_slug, phone });
    return data;
  };

  // Step 2: verify the code and create the account (links children).
  const claim = async (payload) => {
    const { data } = await papi.post("/parent/auth/claim", payload);
    setParent(data.user);
    return data;
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await papi.get("/parent/auth/me");
      setParent(data);
    } catch {}
  }, []);

  const logout = async () => {
    try {
      await papi.post("/parent/auth/logout");
    } catch {}
    setParent(false);
  };

  return (
    <ParentAuthContext.Provider
      value={{ parent, loading, login, loginVerify, claimStart, claim, logout, refresh, formatApiErrorDetail }}
    >
      {children}
    </ParentAuthContext.Provider>
  );
}
